import React from "react";
import { cn } from "../../lib/utils";
import "./cta-button.css";

export interface CtaButtonProps {
  children: React.ReactNode;
  onClick?: React.MouseEventHandler;
  /** Render as a link instead of a button. */
  href?: string;
  target?: string;
  rel?: string;
  type?: "button" | "submit" | "reset";
  disabled?: boolean;
  /** Class on the inner interactive element (for sizing/typography). */
  className?: string;
  /** Class on the outer wrapper (layout, width). */
  glassClassName?: string;
  "aria-label"?: string;
  "aria-busy"?: boolean;
}

/**
 * CtaButton: the solid brand CTA. Powder-blue fill, navy label, 6px corners,
 * a hard press instead of a float. Replaced the WebGL LiquidGlassButton on
 * customer-facing CTAs, which read as a soap bubble. Same props so callers
 * swap without layout changes (glassClassName is kept as the wrapper class).
 */
const CtaButton: React.FC<CtaButtonProps> = ({
  children,
  onClick,
  href,
  target,
  rel,
  type = "button",
  disabled,
  className,
  glassClassName,
  ...rest
}) => (
  <span className={cn("bb-cta", glassClassName)}>
    {href ? (
      <a
        href={href}
        target={target}
        rel={rel}
        onClick={onClick as React.MouseEventHandler<HTMLAnchorElement>}
        className={cn("lg-btn__inner bb-cta__inner", className)}
        {...rest}
      >
        {children}
      </a>
    ) : (
      <button
        type={type}
        disabled={disabled}
        onClick={onClick as React.MouseEventHandler<HTMLButtonElement>}
        className={cn("lg-btn__inner bb-cta__inner", className)}
        {...rest}
      >
        {children}
      </button>
    )}
  </span>
);

export default CtaButton;
